import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import WhatsAppButton from './components/ui/WhatsAppButton';
import PageLoader from './components/ui/PageLoader';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { InquiryProvider, useInquiry } from './context/InquiryContext';
import { NichesProvider } from './context/NichesContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';

// Public pages
const Home            = lazy(() => import('./pages/Home'));
const Marketplace     = lazy(() => import('./pages/Marketplace'));
const CreatorProfile  = lazy(() => import('./pages/CreatorProfile'));
const CaseStudies     = lazy(() => import('./pages/CaseStudies'));
const CaseStudyDetail = lazy(() => import('./pages/CaseStudyDetail'));
const Services        = lazy(() => import('./pages/Services'));
const ForBrands       = lazy(() => import('./pages/ForBrands'));
const About           = lazy(() => import('./pages/About'));
const Contact         = lazy(() => import('./pages/Contact'));
const JoinAsCreator   = lazy(() => import('./pages/JoinAsCreator'));
const NotFound        = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService  = lazy(() => import('./pages/TermsOfService'));

// Admin pages
const AdminLogin        = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout       = lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard         = lazy(() => import('./pages/admin/Dashboard'));
const AdminCreators     = lazy(() => import('./pages/admin/AdminCreators'));
const AdminCases        = lazy(() => import('./pages/admin/AdminCases'));
const AdminServices     = lazy(() => import('./pages/admin/AdminServices'));
const AdminContacts     = lazy(() => import('./pages/admin/AdminContacts'));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials'));
const AdminSettings     = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAdmins       = lazy(() => import('./pages/admin/AdminAdmins'));
const AdminInquiryUsers = lazy(() => import('./pages/admin/AdminInquiryUsers'));

// Inquiry portal pages
const InquiryLogin   = lazy(() => import('./pages/inquiry/InquiryLogin'));
const InquiriesPanel = lazy(() => import('./pages/inquiry/InquiriesPanel'));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RequireAuth({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <PageLoader />;
  if (!admin) return <Navigate to="/admin" replace />;
  return children;
}

function RequireInquiryAuth({ children }) {
  const { user, loading } = useInquiry();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/inquiries/login" replace />;
  return children;
}

export default function App() {
  return (
    <SiteSettingsProvider>
      <NichesProvider>
    <AdminProvider>
      <InquiryProvider>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* ── ADMIN LOGIN ── */}
            <Route path="/admin" element={<AdminLogin />} />

            {/* ── ADMIN PANEL ── */}
            <Route path="/admin/dashboard"     element={<RequireAuth><AdminLayout page={<Dashboard />} /></RequireAuth>} />
            <Route path="/admin/creators"      element={<RequireAuth><AdminLayout page={<AdminCreators />} /></RequireAuth>} />
            <Route path="/admin/cases"         element={<RequireAuth><AdminLayout page={<AdminCases />} /></RequireAuth>} />
            <Route path="/admin/services"      element={<RequireAuth><AdminLayout page={<AdminServices />} /></RequireAuth>} />
            <Route path="/admin/contacts"      element={<RequireAuth><AdminLayout page={<AdminContacts />} /></RequireAuth>} />
            <Route path="/admin/testimonials"  element={<RequireAuth><AdminLayout page={<AdminTestimonials />} /></RequireAuth>} />
            <Route path="/admin/admins"        element={<RequireAuth><AdminLayout page={<AdminAdmins />} /></RequireAuth>} />
            <Route path="/admin/settings"      element={<RequireAuth><AdminLayout page={<AdminSettings />} /></RequireAuth>} />
            <Route path="/admin/inquiry-users" element={<RequireAuth><AdminLayout page={<AdminInquiryUsers />} /></RequireAuth>} />

            {/* ── INQUIRY PORTAL ── */}
            <Route path="/inquiries/login" element={<InquiryLogin />} />
            <Route path="/inquiries"       element={<RequireInquiryAuth><InquiriesPanel /></RequireInquiryAuth>} />

            {/* ── PUBLIC WEBSITE ── */}
            <Route path="/"                   element={<><Navbar /><Suspense fallback={<PageLoader/>}><Home /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/creators"           element={<><Navbar /><Suspense fallback={<PageLoader/>}><Marketplace /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/creators/:slug"     element={<><Navbar /><Suspense fallback={<PageLoader/>}><CreatorProfile /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/case-studies"       element={<><Navbar /><Suspense fallback={<PageLoader/>}><CaseStudies /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/case-studies/:slug" element={<><Navbar /><Suspense fallback={<PageLoader/>}><CaseStudyDetail /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/services"           element={<><Navbar /><Suspense fallback={<PageLoader/>}><Services /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/for-brands"         element={<><Navbar /><Suspense fallback={<PageLoader/>}><ForBrands /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/about"              element={<><Navbar /><Suspense fallback={<PageLoader/>}><About /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/contact"            element={<><Navbar /><Suspense fallback={<PageLoader/>}><Contact /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/join-as-creator"    element={<><Navbar /><Suspense fallback={<PageLoader/>}><JoinAsCreator /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/privacy"            element={<><Navbar /><Suspense fallback={<PageLoader/>}><PrivacyPolicy /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="/terms"              element={<><Navbar /><Suspense fallback={<PageLoader/>}><TermsOfService /></Suspense><Footer /><WhatsAppButton /></>} />
            <Route path="*"                   element={<><Navbar /><Suspense fallback={<PageLoader/>}><NotFound /></Suspense><Footer /></>} />

          </Routes>
        </Suspense>
      </InquiryProvider>
    </AdminProvider>
      </NichesProvider>
    </SiteSettingsProvider>
  );
}
